import { useEffect, useState, type CSSProperties } from "react";
import type { WizardController } from "../../../hooks/use-wrapped-api-wizard";
import {
  useWrappedApi,
  useWrappedApiMappings,
  useSuggestEntityTypeForResponse,
  callWrapperTool,
  type WizardMappingProposal,
  type EntityTypeFieldProposal,
} from "../../../hooks/use-wrapped-apis";
import { useEntityTypes, useCreateEntityType } from "../../../../lib/hooks/use-kuvio";
import type { FieldType } from "../../../../lib/types";
import { showToast } from "../../primitives";
import { MappingEditor } from "../MappingEditor";
import { wizardStyles, ConfidenceBadge, MethodBadge } from "./wizard-ui";

/**
 * Step 4 — Mapping. For each read operation the AI proposes which entity type its
 * response should populate (an existing type, or a brand-new one). The owner maps
 * it via the Sprint 16c MappingEditor (pre-filled with the AI field map), or
 * accepts a proposed new type — created inline via create_entity_type — then maps.
 */
export function Step4Mapping({ wizard }: { wizard: WizardController }) {
  const detail = useWrappedApi(wizard.slug);
  const mappings = useWrappedApiMappings(wizard.slug);
  const [editorFor, setEditorFor] = useState<string | null>(null);
  const [newTypeFor, setNewTypeFor] = useState<string | null>(null);

  const operations = detail.data?.operations ?? [];
  const proposalsByOp = new Map(wizard.proposal?.mapping_proposals.map((m) => [m.operation_id, m]) ?? []);
  const mappedOps = new Set((mappings.data ?? []).map((m) => m.operation_id));

  const editorOp = editorFor ? operations.find((o) => o.operation_key === editorFor) : null;
  const editorProposal = editorFor ? proposalsByOp.get(editorFor) : undefined;
  const editorExisting = editorFor ? (mappings.data ?? []).find((m) => m.operation_id === editorFor) : undefined;

  return (
    <div>
      <p style={wizardStyles.intro}>
        Decide how each read operation's results materialise into structured Kuvio entities. The AI proposes a target
        type and field map — accept it, edit it, or create a new type. Operations you skip just return JSON.
      </p>

      {detail.isLoading ? (
        <div style={wizardStyles.hint}>Loading operations…</div>
      ) : wizard.mappableOps.length === 0 ? (
        <div style={wizardStyles.hint}>No read operations to map.</div>
      ) : (
        wizard.mappableOps.map((op) => {
          const proposal = proposalsByOp.get(op.operation_id);
          const mapped = mappedOps.has(op.operation_id);
          return (
            <div key={op.operation_id} style={card}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <MethodBadge method={op.method} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={opName} title={op.path}>{op.operation_id}</div>
                  <div style={opSummary}>{op.summary}</div>
                </div>
                {mapped && (
                  <span style={mappedBadge}>
                    <i className="ti ti-check" style={{ fontSize: 11 }} aria-hidden="true" /> mapped
                  </span>
                )}
              </div>

              {proposal && !mapped && <ProposalLine proposal={proposal} />}

              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                {mapped ? (
                  <button type="button" style={secondaryBtn} onClick={() => setEditorFor(op.operation_id)}>
                    Edit mapping
                  </button>
                ) : proposal?.target_entity_type_slug === "new" ? (
                  <button type="button" style={primaryGhost} onClick={() => setNewTypeFor(op.operation_id)}>
                    <i className="ti ti-plus" style={{ fontSize: 12 }} aria-hidden="true" /> Create type & map
                  </button>
                ) : (
                  <button type="button" style={primaryGhost} onClick={() => setEditorFor(op.operation_id)}>
                    {proposal ? "Review & map" : "Map manually"}
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}

      {editorFor && editorOp && (
        <MappingEditor
          slug={wizard.slug}
          operations={operations}
          presetOperationId={editorFor}
          existing={editorExisting}
          seed={
            editorProposal && editorProposal.target_entity_type_slug !== "new"
              ? {
                  target_entity_type_slug: editorProposal.target_entity_type_slug,
                  field_map: editorProposal.field_map,
                  sync_mode: editorProposal.sync_mode,
                }
              : undefined
          }
          onClose={() => {
            setEditorFor(null);
            void mappings.refetch();
          }}
        />
      )}

      {newTypeFor && (
        <NewTypeDrawer
          wrapperSlug={wizard.slug}
          operationId={newTypeFor}
          toolName={operations.find((o) => o.operation_key === newTypeFor)?.name ?? null}
          onClose={() => setNewTypeFor(null)}
          onCreated={(typeSlug) => {
            setNewTypeFor(null);
            // Re-point the proposal at the freshly created type and open the editor.
            const prop = proposalsByOp.get(newTypeFor);
            if (prop) prop.target_entity_type_slug = typeSlug;
            setEditorFor(newTypeFor);
          }}
        />
      )}
    </div>
  );
}

function ProposalLine({ proposal }: { proposal: WizardMappingProposal }) {
  const isNew = proposal.target_entity_type_slug === "new";
  return (
    <div style={proposalBox}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
        <i className="ti ti-sparkles" style={{ fontSize: 12, color: "var(--color-text-info)" }} aria-hidden="true" />
        <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
          AI proposes →{" "}
          <strong style={{ fontFamily: "var(--font-mono)" }}>
            {isNew ? "new entity type" : proposal.target_entity_type_slug}
          </strong>{" "}
          · {proposal.sync_mode}
        </span>
        <ConfidenceBadge confidence={proposal.confidence} />
      </div>
      {proposal.reason && <div style={wizardStyles.reason}>{proposal.reason}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New entity type (S16D.13) — infer fields from the response, edit, create.
// ---------------------------------------------------------------------------

const FIELD_TYPES: FieldType[] = [
  "text", "email", "phone", "richtext", "number", "currency", "date", "datetime", "enum", "reference", "references", "document",
];

function idPrefixFrom(slug: string): string {
  const letters = slug.replace(/[^a-zA-Z]/g, "").toUpperCase();
  return letters.slice(0, 4) || "REC";
}

function NewTypeDrawer({
  wrapperSlug,
  operationId,
  toolName,
  onClose,
  onCreated,
}: {
  wrapperSlug: string;
  operationId: string;
  toolName: string | null;
  onClose: () => void;
  onCreated: (typeSlug: string) => void;
}) {
  const suggest = useSuggestEntityTypeForResponse();
  const createType = useCreateEntityType();
  const entityTypes = useEntityTypes();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [fields, setFields] = useState<EntityTypeFieldProposal[]>([]);
  const [sampling, setSampling] = useState(false);
  const [loaded, setLoaded] = useState(false);

  async function runSuggest(sample?: unknown) {
    try {
      const p = await suggest.mutateAsync({ wrapper_slug: wrapperSlug, operation_id: operationId, sample_response: sample });
      setName(p.proposed_name);
      setSlug(p.proposed_type_slug);
      setFields(p.proposed_fields);
      setLoaded(true);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't infer a type", "error", true);
      setLoaded(true);
    }
  }

  // Infer from the operation summary on open; the owner can fetch a live sample to improve it.
  useEffect(() => {
    void runSuggest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchSample() {
    if (!toolName) return;
    setSampling(true);
    const res = await callWrapperTool(toolName, {});
    setSampling(false);
    if (!res.ok) {
      showToast(res.error ?? "Sample call failed — set your credential first", "error", true);
      return;
    }
    await runSuggest(res.body);
    showToast("Re-inferred fields from a live sample");
  }

  function setField(i: number, patch: Partial<EntityTypeFieldProposal>) {
    setFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  }
  function addField() {
    setFields((prev) => [...prev, { name: "", type: "text" }]);
  }
  function removeField(i: number) {
    setFields((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function create() {
    const cleanSlug = slug.trim().toLowerCase();
    if (!/^[a-z][a-z0-9-]*[a-z0-9]$/.test(cleanSlug)) {
      return showToast("Slug must be lowercase letters/digits/dashes (min 2 chars).", "error", true);
    }
    if (entityTypes.data?.some((t) => t.slug === cleanSlug)) {
      return showToast(`A type "${cleanSlug}" already exists — map to it instead.`, "error", true);
    }
    const cleanFields = fields
      .map((f) => ({ ...f, name: f.name.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_") }))
      .filter((f) => /^[a-z][a-z0-9_]*$/.test(f.name));
    if (cleanFields.length === 0) return showToast("Add at least one valid field.", "error", true);
    if (!cleanFields.some((f) => f.display_field)) cleanFields[0]!.display_field = true;

    try {
      await createType.mutateAsync({
        slug: cleanSlug,
        name: name.trim() || cleanSlug,
        id_prefix: idPrefixFrom(cleanSlug),
        data_class: "master",
        fields: cleanFields,
      });
      await entityTypes.refetch();
      showToast(`Created entity type "${name.trim() || cleanSlug}"`);
      onCreated(cleanSlug);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't create the type", "error", true);
    }
  }

  return (
    <div style={overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label="Create entity type" style={drawer}>
        <div style={drawerHead}>
          <h3 style={{ fontSize: 15, fontWeight: 500, margin: 0 }}>New entity type</h3>
          <button type="button" onClick={onClose} style={closeBtn} aria-label="Close">
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden="true" />
          </button>
        </div>

        <div style={drawerBody}>
          {!loaded ? (
            <div style={wizardStyles.hint}>
              <i className="ti ti-loader-2 ti-spin" style={{ fontSize: 13, marginRight: 6 }} aria-hidden="true" />
              Inferring a schema from the operation…
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 12 }}>
                <label style={fieldWrap}>
                  <span style={wizardStyles.label}>Name</span>
                  <input
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, ""));
                    }}
                    style={wizardStyles.input}
                  />
                </label>
                <label style={fieldWrap}>
                  <span style={wizardStyles.label}>Slug</span>
                  <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} style={{ ...wizardStyles.input, fontFamily: "var(--font-mono)" }} />
                </label>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "8px 0 6px" }}>
                <span style={wizardStyles.sectionTitle}>Fields</span>
                {toolName && (
                  <button type="button" onClick={fetchSample} disabled={sampling} style={sampleBtn}>
                    {sampling ? "Fetching…" : "Improve from live sample"}
                  </button>
                )}
              </div>

              {fields.map((f, i) => (
                <div key={i} style={fieldRow}>
                  <input
                    value={f.name}
                    onChange={(e) => setField(i, { name: e.target.value })}
                    placeholder="field_name"
                    style={{ ...wizardStyles.input, flex: 1, fontFamily: "var(--font-mono)", fontSize: 12 }}
                  />
                  <select value={f.type} onChange={(e) => setField(i, { type: e.target.value as FieldType })} style={{ ...wizardStyles.input, width: 120, fontSize: 12 }}>
                    {FIELD_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setFields((prev) => prev.map((x, idx) => ({ ...x, display_field: idx === i })))}
                    title="Display field (the row title)"
                    style={displayToggle(!!f.display_field)}
                  >
                    <i className="ti ti-star" style={{ fontSize: 12 }} aria-hidden="true" />
                  </button>
                  <button type="button" onClick={() => removeField(i)} style={iconBtn} aria-label="Remove field">
                    <i className="ti ti-x" style={{ fontSize: 13 }} aria-hidden="true" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addField} style={addFieldBtn}>
                <i className="ti ti-plus" style={{ fontSize: 12 }} aria-hidden="true" /> Add field
              </button>
            </>
          )}
        </div>

        <div style={drawerFoot}>
          <button type="button" onClick={onClose} style={ghostBtn}>Cancel</button>
          <button type="button" onClick={create} disabled={!loaded || createType.isPending} style={createBtn}>
            {createType.isPending ? "Creating…" : "Create type & map"}
          </button>
        </div>
      </div>
    </div>
  );
}

const card: CSSProperties = {
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  padding: "10px 12px",
  marginBottom: 10,
};
const opName: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-mono)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const opSummary: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const proposalBox: CSSProperties = {
  marginTop: 8,
  padding: "6px 10px",
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-sm)",
};
const mappedBadge: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  fontSize: 10,
  color: "var(--c-green-800, var(--color-text-secondary))",
  background: "var(--c-green-50, var(--color-background-secondary))",
  borderRadius: "var(--border-radius-sm)",
  padding: "2px 7px",
  flexShrink: 0,
};
const secondaryBtn: CSSProperties = {
  fontSize: 12,
  padding: "5px 12px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const primaryGhost: CSSProperties = {
  ...secondaryBtn,
  color: "var(--color-text-info)",
  borderColor: "var(--color-text-info)",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
};
const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 210,
  fontFamily: "var(--font-sans)",
  padding: 24,
};
const drawer: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-secondary)",
  borderRadius: "var(--border-radius-lg)",
  maxWidth: 560,
  width: "100%",
  maxHeight: "85vh",
  display: "flex",
  flexDirection: "column",
};
const drawerHead: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px 12px",
  borderBottom: "0.5px solid var(--color-border-secondary)",
};
const drawerBody: CSSProperties = { padding: "16px 20px", overflowY: "auto", flex: 1 };
const drawerFoot: CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  padding: "12px 20px",
  borderTop: "0.5px solid var(--color-border-secondary)",
};
const fieldWrap: CSSProperties = { display: "flex", flexDirection: "column", gap: 4, flex: 1, marginBottom: 8 };
const fieldRow: CSSProperties = { display: "flex", alignItems: "center", gap: 6, marginBottom: 6 };
const closeBtn: CSSProperties = { border: "none", background: "none", cursor: "pointer", color: "var(--color-text-tertiary)", padding: 4, display: "inline-flex" };
const sampleBtn: CSSProperties = {
  fontSize: 11,
  padding: "3px 10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const iconBtn: CSSProperties = {
  width: 26,
  flexShrink: 0,
  border: "none",
  background: "none",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};
const addFieldBtn: CSSProperties = {
  fontSize: 12,
  padding: "5px 10px",
  marginTop: 4,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const ghostBtn: CSSProperties = {
  fontSize: 13,
  padding: "7px 14px",
  borderRadius: "var(--border-radius-sm)",
  border: "none",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const createBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "7px 16px",
  borderRadius: "var(--border-radius-sm)",
  border: "none",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
function displayToggle(on: boolean): CSSProperties {
  return {
    width: 28,
    flexShrink: 0,
    border: on ? "1px solid var(--color-text-info)" : "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: on ? "var(--c-blue-50)" : "var(--color-background-primary)",
    color: on ? "var(--color-text-info)" : "var(--color-text-tertiary)",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px 0",
  };
}
